import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Sidebar } from "@/components/sidebar";
import { ToastProvider, useToast } from "@/components/action-feedback";
import { NotificationsPopover } from "@/components/notifications-popover";
import { LinkedAccounts } from "@/components/linked-accounts";
import { MonitoringProcessingWatcher } from "@/app/(app)/monitoring/_components/monitoring-processing-watcher";
import { getAuthToken, subscribeAuthToken, getApiBaseUrl } from "@/src/services/base_service";
import { bootstrapApiConfig } from "@/src/services/info_service";
import { bootstrapToken, bootstrapSessionToken } from "@/src/services/auth_service";
import { listNotifications } from "@/src/services/notifications_service";
import { identifyUser, groupCompany, capturePageview } from "@/src/lib/posthog";

const PUBLIC_ROUTES = [
  "/login",
  "/verify-otp",
  "/signup",
  "/signup/password",
  "/forgot-password",
  "/reset-password",
];

const HIDDEN_SIDEBAR_ROUTES = [...PUBLIC_ROUTES, "/welcomedashboard", "/data-processing-agreement"];
const MOBILE_QUERY = "(max-width: 767.98px)";
const WIDE_QUERY = "(min-width: 1280px)";

/** Shows the shared toast each time `signal` changes. Lives inside
 *  ToastProvider because AppLayout renders the provider itself and so can't
 *  call useToast() directly. */
function NewNotificationsToast({ signal }: { signal: number }) {
  const showToast = useToast();
  const lastSignal = useRef(signal);

  useEffect(() => {
    if (signal === lastSignal.current) return;
    lastSignal.current = signal;
    showToast({ tone: "success", text: "New notifications in your menu." });
  }, [signal, showToast]);

  return null;
}

function matchesAny(pathname: string, routes: string[]) {
  return routes.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

function resolvePageName(pathname: string): string | undefined {
  if (pathname === "/dashboard") return "Dashboard";
  if (pathname === "/screening-portal") return "Single Screening";
  if (pathname === "/batch") return "Batch List";
  if (/^\/batch\/[^/]+\/search\//.test(pathname)) return "Batch Screening";
  if (/^\/batch\/[^/]+/.test(pathname)) return "Batch Detail";
  if (pathname === "/monitoring-portal") return "Monitoring";
  if (pathname === "/monitoring-portal/new") return "New Monitoring";
  if (/^\/monitoring-portal\/[^/]+/.test(pathname)) return "Monitoring Entry";
  if (pathname === "/account-management") return "Account settings";
  if (pathname === "/company-settings") return "Company settings";
  if (pathname === "/api-settings") return "API & Webhooks settings";
  if (pathname === "/login") return "Login";
  if (pathname === "/signup") return "Register";
  if (pathname === "/help") return "Help";
  if (pathname === "/welcomedashboard") return "Welcome";
  return undefined;
}

export type AppOutletContext = {
  apiUser: { name: string | null; email: string; optOut: boolean; accountType: string } | null;
  infoResolved: boolean;
  enableMonitoring: boolean;
  adverseMediaEnabled: boolean;
  pepAllowed: boolean;
  hasActivePlan: boolean;
  mainInsetLeft: number;
  markOnboardingOptedOut: () => void;
};

export default function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = useSyncExternalStore(subscribeAuthToken, getAuthToken, getAuthToken);
  const authed = !!token;
  const [initialized, setInitialized] = useState(false);
  const [apiUser, setApiUser] = useState<{
    name: string | null;
    email: string;
    optOut: boolean;
    accountType: string;
  } | null>(null);
  const [specificLogo, setSpecificLogo] = useState<string | undefined>(undefined);
  const [infoResolved, setInfoResolved] = useState(false);
  const [enableMonitoring, setEnableMonitoring] = useState(true);
  const [adverseMediaEnabled, setAdverseMediaEnabled] = useState(true);
  const [pepAllowed, setPepAllowed] = useState(true);
  const [hasActivePlan, setHasActivePlan] = useState(true);
  const [ssoGate, setSsoGate] = useState<{ enforceSso: boolean; userSsoEnabled: boolean } | null>(
    null,
  );
  const [staffVersionInfo, setStaffVersionInfo] = useState<{
    backendVersion: string;
    adminUrl: string;
  } | null>(null);
  const [notifUnread, setNotifUnread] = useState(0);
  const [notifReloadKey, setNotifReloadKey] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const isWide = useMediaQuery(WIDE_QUERY);
  const [manualCompact, setManualCompact] = useState<boolean | null>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const notifTriggerRef = useRef<HTMLButtonElement>(null);
  const seenNotifIds = useRef<Set<string> | null>(null);
  const [newNotifSignal, setNewNotifSignal] = useState(0);

  useEffect(() => {
    let active = true;
    if (bootstrapToken()) {
      setInitialized(true);
      return;
    }
    void bootstrapSessionToken().finally(() => {
      if (active) setInitialized(true);
    });
    return () => {
      active = false;
    };
  }, []);

  const markOnboardingOptedOut = useCallback(() => {
    setApiUser((prev) => (prev ? { ...prev, optOut: true } : prev));
  }, []);

  const refreshNotifUnread = useCallback(() => {
    void listNotifications()
      .then((res) => {
        if (!res.ok) return;
        const unread = res.value.filter((n) => !n.read);
        setNotifUnread(unread.length);

        const allIds = new Set(res.value.map((n) => n.id));
        if (seenNotifIds.current === null) {
          seenNotifIds.current = allIds;
        } else {
          const fresh = unread.filter((n) => !seenNotifIds.current!.has(n.id));
          seenNotifIds.current = allIds;
          if (fresh.length > 0) {
            setNewNotifSignal((n) => n + 1);
            setNotifReloadKey((k) => k + 1);
          }
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!authed) return;
    void bootstrapApiConfig()
      .then((info) => {
        setApiUser({
          name: info.user.name,
          email: info.user.email,
          optOut: info.user.optOut,
          accountType: info.user.accountType,
        });
        setEnableMonitoring(info.company.enableMonitoring && info.user.enableMonitoring);
        setAdverseMediaEnabled(info.company.adverseMediaEnabled);
        setPepAllowed(info.company.pepAllowed);
        setHasActivePlan(info.company.hasActivePlan);
        setSpecificLogo(info.company.specificLogoUrl);
        setSsoGate({ enforceSso: info.company.enforceSso, userSsoEnabled: info.user.ssoEnabled });
        if (info.user.isStaff) {
          setStaffVersionInfo({
            backendVersion: info.appVersion,
            adminUrl: `${getApiBaseUrl()}/admin-${info.adminZoneUuid}/`,
          });
        }
        identifyUser(info.user.uuid, {
          is_staff: info.user.isStaff,
          created: info.user.created,
          account_type: info.user.accountType,
          sso_enabled: info.user.ssoEnabled,
          has_active_plan: info.user.hasActivePlan,
          company_domain: info.user.email.split("@")[1],
        });
        groupCompany(info.company.id, {
          name: info.company.name,
          has_active_plan: info.company.hasActivePlan,
          enable_monitoring: info.company.enableMonitoring,
          adverse_media_enabled: info.company.adverseMediaEnabled,
          pep_allowed: info.company.pepAllowed,
          enforce_sso: info.company.enforceSso,
        });
      })
      .catch(() => {})
      .finally(() => setInfoResolved(true));
    refreshNotifUnread();
  }, [authed, refreshNotifUnread]);

  // The shell stays mounted across logout → /login, so drop everything derived
  // from the previous user's /info/ + notifications once the bearer is gone.
  useEffect(() => {
    if (authed) return;
    setApiUser(null);
    setSpecificLogo(undefined);
    setInfoResolved(false);
    setEnableMonitoring(true);
    setAdverseMediaEnabled(true);
    setPepAllowed(true);
    setHasActivePlan(true);
    setSsoGate(null);
    setStaffVersionInfo(null);
    setNotifUnread(0);
    setNotifOpen(false);
    seenNotifIds.current = null;
  }, [authed]);

  useEffect(() => {
    if (!authed) return;
    const id = window.setInterval(refreshNotifUnread, 10_000);
    return () => window.clearInterval(id);
  }, [authed, refreshNotifUnread]);

  const prevNotifOpen = useRef(false);
  useEffect(() => {
    if (prevNotifOpen.current && !notifOpen && authed) refreshNotifUnread();
    prevNotifOpen.current = notifOpen;
  }, [notifOpen, authed, refreshNotifUnread]);

  useEffect(() => {
    if (!initialized) return;
    const onPublic = matchesAny(location.pathname, PUBLIC_ROUTES);

    if (!authed) {
      if (!onPublic) navigate("/login", { replace: true });
      return;
    }

    if (location.pathname === "/login" || location.pathname === "/signup") {
      navigate("/", { replace: true });
    }
  }, [initialized, authed, location.pathname, navigate]);

  useEffect(() => {
    setDrawerOpen(false);
    setNotifOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    capturePageview(location.pathname, resolvePageName(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const drawer = drawerRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusables = () =>
      Array.from(
        drawer?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
    focusables()[0]?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setDrawerOpen(false);
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [drawerOpen]);

  useEffect(() => {
    if (!isMobile && drawerOpen) setDrawerOpen(false);
  }, [isMobile, drawerOpen]);

  useEffect(() => {
    setManualCompact(null);
  }, [isWide]);

  if (!initialized) {
    return null;
  }

  if (matchesAny(location.pathname, HIDDEN_SIDEBAR_ROUTES)) {
    return (
      <ToastProvider>
        <div className="h-screen overflow-y-auto">
          <Outlet />
        </div>
      </ToastProvider>
    );
  }

  // On mobile the compact rail stays visible; expanding it overlays the content.
  const drawerOverlay = isMobile && drawerOpen;
  const sidebarCompact = isMobile ? !drawerOpen : (manualCompact ?? !isWide);
  const sidebarWidth = isMobile || sidebarCompact ? 52 : 220;

  return (
    <ToastProvider>
      <div className="flex h-screen">
        {drawerOpen && (
          <div
            className="fixed inset-0 z-40 bg-grey-900-24 md:hidden"
            aria-hidden
            onClick={() => setDrawerOpen(false)}
          />
        )}

        <div className={["relative z-50 shrink-0", isMobile ? "w-13" : ""].join(" ")}>
          <div
            ref={drawerRef}
            id="app-nav-drawer"
            role={drawerOverlay ? "dialog" : undefined}
            aria-modal={drawerOverlay ? true : undefined}
            aria-label={drawerOverlay ? "Main navigation" : undefined}
            className={isMobile ? "fixed inset-y-0 left-0" : undefined}
          >
            <Sidebar
              tools={enableMonitoring ? ["screening", "monitoring"] : ["screening"]}
              accountType={apiUser?.accountType}
              userName={apiUser?.name ?? undefined}
              userEmail={apiUser?.email ?? undefined}
              backendVersion={staffVersionInfo?.backendVersion}
              adminUrl={staffVersionInfo?.adminUrl}
              compact={sidebarCompact}
              onToggleCompact={
                isMobile
                  ? () => setDrawerOpen((v) => !v)
                  : () => setManualCompact((c) => !(c ?? !isWide))
              }
              onNavigate={() => setDrawerOpen(false)}
              notificationsOpen={notifOpen}
              onToggleNotifications={() => {
                setDrawerOpen(false);
                setNotifOpen((v) => !v);
              }}
              notificationsButtonRef={notifTriggerRef}
              notificationsUnreadCount={notifUnread}
              specificLogo={specificLogo}
            />
          </div>
        </div>

        <div className="min-w-0 flex-1 overflow-y-auto">
          <Outlet
            context={
              {
                apiUser,
                infoResolved,
                enableMonitoring,
                adverseMediaEnabled,
                pepAllowed,
                hasActivePlan,
                mainInsetLeft: sidebarWidth,
                markOnboardingOptedOut,
              } satisfies AppOutletContext
            }
          />
        </div>

        <NotificationsPopover
          open={notifOpen}
          onClose={() => setNotifOpen(false)}
          anchorEl={notifTriggerRef.current}
          isMobile={isMobile}
          scrimInsetLeft={sidebarWidth}
          onUnreadCount={setNotifUnread}
          reloadKey={notifReloadKey}
        />

        {/* Wait for /info/: until it hydrates the monitoring base URL, every
            monitoring request hits the SPA's own origin and reads as "no entries". */}
        <MonitoringProcessingWatcher enabled={authed && infoResolved && enableMonitoring} />

        <NewNotificationsToast signal={newNotifSignal} />

        {ssoGate?.enforceSso && !ssoGate.userSsoEnabled && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Connect a single sign-on provider"
            className="fixed inset-0 z-[100] flex items-center justify-center bg-grey-900-24 p-4"
          >
            <div className="w-full max-w-[780px]">
              <LinkedAccounts
                title="Single sign-on required"
                description="Your company has enabled Enforce SSO — connect one of the providers below to keep using your account. Email and password sign-in is disabled."
              />
            </div>
          </div>
        )}
      </div>
    </ToastProvider>
  );
}
